import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { NamedVariantMaterialsEffect } from "./namedVariantMaterialsEffect";
import type { VariantConfig } from "./variantConfig";

export type NamedVariantStatus = "draft" | "active" | "archived";
export type NamedVariantConfirmStatus = "pending" | "confirmed" | "rejected";

export type NamedVariantSummary = {
  id: string;
  code: string;
  name: string;
  description: string;
  status: NamedVariantStatus;
  default_transform_config: VariantConfig;
  pending_application_count: number;
  application_count: number;
  created_at: string;
  updated_at: string;
};

export type NamedVariantApplicationSummary = {
  id: string;
  named_variant_id: string;
  parent_recipe_id: string;
  parent_recipe_code: string;
  parent_recipe_name: string;
  confirm_status: NamedVariantConfirmStatus;
  materials_effect: NamedVariantMaterialsEffect;
  wholesale_price_delta: number | null;
  retail_price_delta: number | null;
  included_by_default: boolean;
  ai_rationale: string | null;
  confirmed_at: string | null;
  sort_order: number;
  notes: string;
  created_at: string;
  updated_at: string;
};

export function listManufacturingNamedVariants() {
  return manufacturingFetch<{ ok: true; variants: NamedVariantSummary[] }>(
    "/api/mfg/named-variants",
    { method: "GET" },
  );
}

export function getManufacturingNamedVariantDetail(namedVariantId: string) {
  const q = new URLSearchParams({ named_variant_id: namedVariantId });
  return manufacturingFetch<{
    ok: true;
    variant: NamedVariantSummary & { default_transform_config: VariantConfig };
    applications: NamedVariantApplicationSummary[];
  }>(`/api/mfg/named-variants/detail?${q.toString()}`, { method: "GET" });
}

export function createManufacturingNamedVariant(payload: {
  code: string;
  name: string;
  description?: string;
  status?: NamedVariantStatus;
  default_transform_config?: VariantConfig;
}) {
  return manufacturingFetch<{ ok: true; variant: NamedVariantSummary }>(
    "/api/mfg/named-variants/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function updateManufacturingNamedVariant(payload: {
  named_variant_id: string;
  code?: string;
  name?: string;
  description?: string;
  status?: NamedVariantStatus;
  default_transform_config?: VariantConfig;
}) {
  return manufacturingFetch<{ ok: true; variant: NamedVariantSummary }>(
    "/api/mfg/named-variants/update",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function deleteManufacturingNamedVariant(payload: { named_variant_id: string }) {
  return manufacturingFetch<{ ok: true }>("/api/mfg/named-variants/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function listManufacturingNamedVariantApplicationsForParent(parentRecipeId: string) {
  const q = new URLSearchParams({ parent_recipe_id: parentRecipeId });
  return manufacturingFetch<{ ok: true; applications: NamedVariantApplicationSummary[] }>(
    `/api/mfg/named-variants/applications-for-parent?${q.toString()}`,
    { method: "GET" },
  );
}

export function updateManufacturingNamedVariantApplication(payload: {
  application_id: string;
  confirm_status?: NamedVariantConfirmStatus;
  materials_effect?: NamedVariantMaterialsEffect;
  wholesale_price_delta?: number | null;
  retail_price_delta?: number | null;
  included_by_default?: boolean;
  notes?: string;
}) {
  return manufacturingFetch<{ ok: true; application: NamedVariantApplicationSummary }>(
    "/api/mfg/named-variants/applications/update",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function deleteManufacturingNamedVariantApplication(payload: { application_id: string }) {
  return manufacturingFetch<{ ok: true }>("/api/mfg/named-variants/applications/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function applyManufacturingNamedVariantToRecipes(payload: {
  named_variant_id: string;
  parent_recipe_ids: string[];
  description: string;
}) {
  return manufacturingFetch<{
    ok: true;
    used_ai: boolean;
    applications: NamedVariantApplicationSummary[];
  }>("/api/mfg/named-variants/apply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Re-materialize catalog default onto all applications; unconfirm for review. */
export function resetManufacturingNamedVariantApplications(payload: {
  named_variant_id: string;
}) {
  return manufacturingFetch<{ ok: true; reset_count: number }>(
    "/api/mfg/named-variants/reset",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export type MfgNamedVariantsBulkUpsertSummary = {
  created: Array<{ variant_code: string; variant_id: string }>;
  updated: Array<{ variant_code: string; variant_id: string }>;
  skipped: Array<{ variant_code: string; reason: string }>;
};

export function bulkUpsertManufacturingNamedVariants(payload: {
  variants: Array<{
    variant_code: string;
    variant_name: string;
    description?: string;
    status?: NamedVariantStatus;
  }>;
}) {
  return manufacturingFetch<{ ok: true; summary: MfgNamedVariantsBulkUpsertSummary }>(
    "/api/mfg/named-variants/bulk-upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function previewManufacturingNamedVariantApplication(applicationId: string) {
  const q = new URLSearchParams({ application_id: applicationId });
  return manufacturingFetch<{
    ok: true;
    lines: Array<{
      material_code: string;
      material_name: string;
      quantity: number;
      slot_label: string;
      unit_cost: number;
      extended_cost: number;
      is_changed?: boolean;
      replaced_material_code?: string;
      replaced_material_name?: string;
    }>;
    warnings: string[];
  }>(`/api/mfg/named-variants/applications/preview?${q.toString()}`, { method: "GET" });
}
